import { isAvatarSrc, socialBackgroundWithFallback, thumbnailMediumBlue } from '@utils/thumbnails'
import { useAuthContext } from '@contexts/authContext'
import useModalContext from '@contexts/modalContext'
import { fontSize, size } from '@styles/theme'
import { formatDistance } from 'date-fns'
import styled from '@emotion/styled'
import clamp from 'clamp-js'

import { PiCalendarBlank, PiChatCircleTextBold, PiDotBold, PiMapPin } from 'react-icons/pi'
import { FollowsYouIndicator } from './elements/FollowsYouIndicator'
import ProfileNAvigateAvatar from '@elements/ProfileNavigateAvatar'
import { SocialProfileTabs } from './elements/SocialProfileTabs'
import { SubscriberIcon } from '@elements/icons/SubscriberIcon'
import { TopButton } from './SocialElements'
import FollowButton from './FollowButton'

const ProfileMain = styled.div`
  padding-bottom: ${size(1)};
`

const BackgroundImage = styled.div`
  width: 100%;
  background-color: #333333;
  height: 17.5dvh;
  background-size: cover;
  background-position: center;
  background-image: url(${props => props.src});
`

const Gradient = styled.div`
  height 100%,
  width: 100%;
  position: absolute;
  bottom: ${size(-20)};
  left: 0;
  background-color: black;
`

const TopContainer = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  width: 100%;
  padding: ${size(1.5)} ${size(1.5)} ${size(2)} ${size(1.5)};
  background-color: black;
  &::before {
    content: '';
    height: ${size(45)};
    width: 100%;
    background: linear-gradient(180deg, #000 50%, rgba(0, 0, 0, 0.00) 100%);
    position: absolute;
    bottom: 0;
    left: 0;
    transform: translateY(100%);
    z-index: -1;
  }
`

const ProfileActions = styled.div`
  position: relative;
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  width: 100%;
`

const RightActions = styled.div`
  display: flex;
  align-items: center;
  min-height: ${size(4)};
`

// TODO: Make this fade in for a better usr experience
const ProfileImage = styled(ProfileNAvigateAvatar)`
  position: absolute;
  bottom: 0;
  left: 0;
  transform: translateY(35%);
  height: ${size(10)};
  width: ${size(10)};
  border: 4px solid black;
`
  
const TopButtonIcon = styled(TopButton)`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: ${size(0.5)};
  borderRadius: 100%;
  height: 32px;
  width: 32px;
`

const DisplayName = styled.h2`
  display: flex;
  align-items: center;
  margin: ${size(0.75)} 0 ${size(0.5)} 0;
  font-size: ${size(2.5)};
  letter-spacing: 0.35px;
`

const Handle = styled.span`
  display: flex;
  gap: ${size(0.5)};
  flex-wrap: wrap;
  color: grey;
  margin-bottom: ${size(1.5)};
  font-size: ${fontSize('sm')};
  font-weight: 500;
  letter-spacing: 0.35px;
`
  
const Bio = styled.span`
  margin-bottom: ${size(1.5)};
  font-size: ${fontSize('smd')};
  letter-spacing: 0.35px;
  word-spacing: -1px;
  line-height: ${size(2.5)};
  padding-right: ${size(2)};
`

const MetaContainer = styled.span`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: ${size(1.5)};
  margin-bottom: ${size(1)};
  
  font-size: ${fontSize('sm')};
  letter-spacing: 0.35px;
  word-spacing: -1px;
  
  color: grey;
`

const MetaInfo = styled.span`
  display: flex;
  align-items: center;
  flex-shrink: 0;

  font-size: ${fontSize('sm')};
  letter-spacing: 0.35px;
  
  svg {
    margin-right: ${size(0.35)};
  }
`
  
const Followers = styled.span`
  display: flex;
  align-items: center;
  font-size: ${fontSize('sm')};
  font-weight: 800;
  margin: 0 0 ${size(0.5)} 0;
`

const FollowersText = styled.span`
  color: grey;
  font-weight: 400,
  letter-spacing: 0.35px;
`

const tabStyles = {
  position: 'relative',
  top: 0,
  minHeight: 0,
  borderBottom: '0.5px solid rgba(255,255,255,0.2)',
  backgroundColor: 'black',
  marginBottom: size(1),
  '& .MuiTab-root': {
    // height: size(6),
    minHeight: 0,
    paddingBottom: size(2.5),
  },
}

const SocialProfileMain = ({ selectedTab, setSelectedTab, profile = {}, isOwnProfile, followUser, unfollowUser }) => {
  const { state: authState } = useAuthContext()
  const isAnonymous = authState?.user?.isAnonymous
  const { messagesModal } = useModalContext()
  const isAvatar = isAvatarSrc('medium', profile?.socialMedia?.thumbnail, profile?.defaultAvatar)
  const userThumbnail = thumbnailMediumBlue(profile?.socialMedia?.thumbnail, profile?.defaultAvatar)

  const openMessagesModal = () => {
    messagesModal.open(null, { state: { user: profile, conversationType: 'social' } })
  }
  
  const renderFormattedTime = (datetime) => {
    let time = formatDistance(datetime, Date.now())
    time = time.replace('about ', '')
    return `${time} ago`
  }

  return (
    <ProfileMain>
      <Gradient />
      <BackgroundImage
        src={socialBackgroundWithFallback(profile)}
      />
      <TopContainer>
        <ProfileActions>
          <ProfileImage
            uuid={profile?.uuid}
            src={userThumbnail}
            canNavigate={false}
            style={{ filter: isAvatar && 'grayscale(100%) contrast(120%)' }}
          />
          <RightActions>
            {!isOwnProfile && profile && Object.keys(profile).length > 0 &&
              <>
                <TopButtonIcon
                  disabled={isAnonymous}
                  onClick={() => openMessagesModal()}
                  style={{
                    marginRight: size(1),
                    opacity: isAnonymous ? 0.4 : 1
                  }}>
                  <PiChatCircleTextBold fill='white' size={size(2.25)} />
                </TopButtonIcon>
                <FollowButton
                  unfollowUser={unfollowUser}
                  followUser={followUser}
                  profile={profile}
                />
              </>
            }
          </RightActions>
        </ProfileActions>
        <DisplayName>
          {profile?.displayName}
          {profile?.isSubscribed && (
            <>
              &nbsp;
              <SubscriberIcon
                size={2.25}
                style={{ marginTop: size(-0.4) }}
              />
            </>
          )}
        </DisplayName>
        <Handle>
          @{profile?.handle}
          <FollowsYouIndicator
            isFollowingYou={profile?.followingMe}
          />
        </Handle>
        {profile?.bio &&
          <Bio ref={node => node && clamp(node, { clamp: 5 })}>
            {profile?.bio}
          </Bio>
        }
        {/* Increasing the margin so the bio empty state looks more normal */}
        <MetaContainer style={{ marginBottom: !profile?.bio && size(1.5) }}>
          {profile?.homeLocation &&
            <MetaInfo>
              <PiMapPin fill='grey' size={size(2)} />
              {profile?.homeLocation}
            </MetaInfo>
          }
          {profile?.registeredAt && 
            <MetaInfo>
              <PiCalendarBlank fill='grey' size={size(2)} />
              Joined {renderFormattedTime(profile?.registeredAt)}
            </MetaInfo>
          }
        </MetaContainer>
        <Followers>
          <PiDotBold />
          {profile?.followerCount || 0}
          &nbsp;
          <FollowersText>
            Followers
          </FollowersText>
        </Followers>
      </TopContainer>
      <SocialProfileTabs
        selectedTab={selectedTab}
        setSelectedTab={setSelectedTab}
      />
    </ProfileMain>
  )
}

export default SocialProfileMain
